package com.swetdot.swetgram.ui.auth

import com.swetdot.swetgram.protocol.Constraints

/**
 * Client-side form validation, mirroring the contract's own constraints.
 *
 * The point is not to replace server validation -- the server still decides -- but to tell the
 * user what is wrong BEFORE spending a round trip and, more importantly, before they tap a
 * button and wait. Because it reads the same `Constraints` the wire contract exposes, the two
 * cannot drift: change the contract and this moves with it.
 *
 * Errors are returned as a typed reason rather than a message, so EN and RU strings live in
 * resources and this file never needs to know about locales.
 */
enum class FieldError {
    Required,
    UsernameTooShort,
    UsernameTooLong,
    UsernameBadCharacters,
    UsernameReserved,
    EmailInvalid,
    PasswordTooShort,
    PasswordTooLong,
    PasswordNeedsLetterAndDigit,
    PasswordsDoNotMatch,
    /** The server refused a password without saying which of its rules it broke. */
    PasswordRejected,
    DisplayNameTooLong,
    CodeIncomplete,
    /** The server refused the code: wrong, already spent, or past its 15 minutes. */
    CodeRejected,
}

/**
 * The registration form (SDS §27.4, which lists the fields in this order and includes
 * `Confirm password`).
 *
 * [confirmPassword] never goes on the wire -- `RegisterRequest` has no such property and the
 * contract is frozen. It exists because the password field is masked: a typo in it creates a real
 * account whose password is a string the user has never seen, and the only way back is the
 * recovery flow, which needs the address they may also have mistyped on the same screen.
 */
data class RegisterForm(
    val displayName: String = "",
    val username: String = "",
    val email: String = "",
    val password: String = "",
    val confirmPassword: String = "",
    val acceptedLegal: Boolean = false,
    val ageConfirmed: Boolean = false,
)

data class RegisterErrors(
    val displayName: FieldError? = null,
    val username: FieldError? = null,
    val email: FieldError? = null,
    val password: FieldError? = null,
    val confirmPassword: FieldError? = null,
    val legalRequired: Boolean = false,
    val ageRequired: Boolean = false,
) {
    val isValid: Boolean
        get() = displayName == null && username == null && email == null &&
            password == null && confirmPassword == null && !legalRequired && !ageRequired
}

// Deliberately permissive: strict RFC 5322 rejects addresses that genuinely work, and what
// actually proves an address is a delivered verification email -- which this app already does.
private val emailPattern = Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$")

fun validateRegister(form: RegisterForm): RegisterErrors {
    val name = form.displayName.trim()
    // Normalised before checking, because that is what will be sent -- validating the raw
    // input would reject "Alex_K" while the request would have succeeded.
    val username = form.username.trim().lowercase()
    val email = form.email.trim()

    return RegisterErrors(
        displayName = when {
            name.isEmpty() -> FieldError.Required
            name.length > Constraints.DISPLAY_NAME_MAX -> FieldError.DisplayNameTooLong
            else -> null
        },
        username = validateUsername(username),
        email = validateEmail(email),
        password = validatePassword(form.password),
        confirmPassword = validateConfirmation(form.password, form.confirmPassword),
        legalRequired = !form.acceptedLegal,
        ageRequired = !form.ageConfirmed,
    )
}

/**
 * "Type it again", asked once because it is asked on two screens.
 *
 * Registration and password reset put the same question to the user, and a second spelling of the
 * rule is exactly how the two end up disagreeing -- about whether a trailing space counts, about
 * whether an empty box is a mismatch or a missing field, about which sentence appears underneath.
 * One function means one answer and, through [FieldError.PasswordsDoNotMatch], one message.
 *
 * An empty confirmation is [FieldError.Required] rather than a mismatch: the user has not made a
 * mistake yet, they have simply not finished, and "Both passwords must match" under an untouched
 * box accuses them of something they have not done.
 *
 * Compared raw, with no trimming on either side. A trailing space IS part of a password, so
 * normalising here would let a genuine mismatch through to the server -- and on the reset screen
 * that costs the code.
 */
fun validateConfirmation(password: String, confirmation: String): FieldError? = when {
    confirmation.isEmpty() -> FieldError.Required
    confirmation != password -> FieldError.PasswordsDoNotMatch
    else -> null
}

fun validateEmail(raw: String): FieldError? {
    val email = raw.trim()
    return when {
        email.isEmpty() -> FieldError.Required
        !emailPattern.matches(email) || email.length > Constraints.EMAIL_MAX -> FieldError.EmailInvalid
        else -> null
    }
}

fun validateUsername(raw: String): FieldError? {
    val username = raw.trim().lowercase()
    return when {
        username.isEmpty() -> FieldError.Required
        username.length < Constraints.USERNAME_MIN -> FieldError.UsernameTooShort
        username.length > Constraints.USERNAME_MAX -> FieldError.UsernameTooLong
        // Checked before the character rule so a reserved name gets the accurate message
        // rather than a confusing "invalid characters".
        username in Constraints.RESERVED_USERNAMES -> FieldError.UsernameReserved
        !username.all { it in 'a'..'z' || it in '0'..'9' || it == '_' } -> FieldError.UsernameBadCharacters
        else -> null
    }
}

fun validatePassword(password: String): FieldError? = when {
    password.isEmpty() -> FieldError.Required
    password.length < Constraints.PASSWORD_MIN -> FieldError.PasswordTooShort
    // The upper bound matters as much as the lower one, and only the client can report it
    // usefully: `Constraints.isPasswordValid` fails a 200-character password on the MAX, but the
    // server answers with one message covering both bounds -- so a user pasting a password
    // manager's long string is told it is "at least 10 characters" too short. Verified against
    // the deployed server, which returns exactly that for a 201-character password.
    password.length > Constraints.PASSWORD_MAX -> FieldError.PasswordTooLong
    // The contract requires at least one letter AND one digit.
    !(password.any { it.isLetter() } && password.any { it.isDigit() }) ->
        FieldError.PasswordNeedsLetterAndDigit
    else -> null
}

/**
 * The password-reset form: the emailed code, and the replacement password entered twice.
 *
 * Both this screen and registration ask for the password twice, and for the same reason -- the
 * field is masked, so nobody can proofread it. What differs is the cost of getting it wrong. A
 * mistyped registration password can still be recovered by email; a mistyped one here cannot, or
 * not without starting over, because the reset code is single-use. The server consumes it inside
 * the same transaction that writes the new hash: a probe against the deployed server answered
 * `204` and then `400 {"field":"token"}` when the same code was replayed. So a typo leaves the
 * account on a string nobody has ever seen, with the only code that could fix it already spent.
 */
data class ResetForm(
    val code: String = "",
    val newPassword: String = "",
    val confirmPassword: String = "",
)

data class ResetErrors(
    val code: FieldError? = null,
    val newPassword: FieldError? = null,
    val confirmPassword: FieldError? = null,
) {
    val isValid: Boolean get() = code == null && newPassword == null && confirmPassword == null
}

fun validateReset(form: ResetForm): ResetErrors = ResetErrors(
    code = validateCode(form.code),
    newPassword = validatePassword(form.newPassword),
    // The same [validateConfirmation] registration uses -- deliberately, so the two screens
    // cannot come to different conclusions about the same pair of boxes.
    confirmPassword = validateConfirmation(form.newPassword, form.confirmPassword),
)

/** The verification code is 8 characters from a restricted alphabet, entered as "ABCD-EFGH". */
fun validateCode(raw: String): FieldError? {
    val normalized = raw.uppercase().filter { it.isLetterOrDigit() }
    return when {
        normalized.isEmpty() -> FieldError.Required
        normalized.length < CODE_LENGTH -> FieldError.CodeIncomplete
        else -> null
    }
}

const val CODE_LENGTH = 8

/** Strips the hyphen and any stray spacing before the code goes on the wire. */
fun normalizeCode(raw: String): String = raw.uppercase().filter { it.isLetterOrDigit() }
